import { useQuery, useQueries, QueryKey } from '@tanstack/react-query';
import { NETWORKS, type NetworkKey } from '../config/networks';
import type { NetworkConfig } from '../types/bridge';
import { post, getAliveNodes, getStableAuthorityLink, fetchBridgeFeesConfig, authorityPostBody } from '../utils/bridge';
import type { BridgeFeesConfig, BridgeStats } from '../types/bridge';

const DEFAULTS = {
  refetchInterval: 15000,
  staleTime: 10000,
  gcTime: 5 * 60 * 1000,
  retry: 1,
};

export function useAliveNodesQuery(networkKey: NetworkKey) {
  const network = NETWORKS[networkKey] as NetworkConfig;
  return useQuery<number[]>({
    queryKey: ['aliveNodes', networkKey] as QueryKey,
    queryFn: () => getAliveNodes(network),
    ...DEFAULTS,
  });
}

export function useBridgeFeesQuery(networkKey: NetworkKey) {
  const network = NETWORKS[networkKey] as NetworkConfig;
  return useQuery<BridgeFeesConfig>({
    queryKey: ['bridgeFees', networkKey] as QueryKey,
    queryFn: () => fetchBridgeFeesConfig(network),
    ...DEFAULTS,
  });
}

export function useBridgeStatsQuery(networkKey: NetworkKey) {
  const network = NETWORKS[networkKey] as NetworkConfig;
  return useQuery<BridgeStats | null>({
    queryKey: ['bridgeStats', networkKey] as QueryKey,
    queryFn: async () => {
      try {
        const link = getStableAuthorityLink(network);
        const res = await post(`${link}/stats`, authorityPostBody(network, {}));
        return res.data ?? null;
      } catch {
        return null;
      }
    },
    ...DEFAULTS,
  });
}

export type BridgeOperationalState = 'open' | 'closed' | 'unknown';

/**
 * Operational bridge state derived from the authority /stats poll (no extra
 * request). 'unknown' while loading or if the queried node is unreachable —
 * callers should treat 'unknown' as "don't block", since the daemon itself
 * enforces the closed state on every write.
 */
export function useBridgeState(networkKey: NetworkKey): {
  state: BridgeOperationalState;
  isLoading: boolean;
} {
  const { data, isLoading } = useBridgeStatsQuery(networkKey);
  const mode = data?.bridgeStateConsensus?.databaseMode;
  const state: BridgeOperationalState =
    mode === 'OPEN' ? 'open' : mode === 'CLOSED' ? 'closed' : 'unknown';
  return { state, isLoading };
}

export function useWarmBridgeQueries(networkKeys: NetworkKey[]) {
  useQueries({
    queries: networkKeys.flatMap((key) => [
      {
        queryKey: ['aliveNodes', key] as QueryKey,
        queryFn: () => getAliveNodes(NETWORKS[key] as NetworkConfig),
        ...DEFAULTS,
      },
      {
        queryKey: ['bridgeStats', key] as QueryKey,
        queryFn: async () => {
          try {
            const net = NETWORKS[key] as NetworkConfig;
            const link = getStableAuthorityLink(net);
            const res = await post(`${link}/stats`, authorityPostBody(net, {}));
            return res.data ?? null;
          } catch {
            return null;
          }
        },
        ...DEFAULTS,
      },
    ]),
  });
}

